import { useState, type ReactNode } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { NavRail } from './NavRail'
import { TopBar } from './TopBar'
import { CommandPalette } from './CommandPalette'
import { PlanStrip } from './PlanStrip'
import { TodayShout } from '@/components/partner/TodayShout'

export interface AppShellProps {
  children: ReactNode
}

/** Left rail (desktop) / bottom tab bar (mobile) + top bar + content column, per the design guide. */
export function AppShell({ children }: AppShellProps) {
  const { slug } = useParams<{ slug: string }>()
  const { pathname } = useLocation()
  const [paletteOpen, setPaletteOpen] = useState(false)

  return (
    <div className="flex min-h-dvh bg-bg">
      {/* Keyboard users jump past the rail and top bar straight to the page. */}
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-surface-pop px-3 py-2 text-small font-medium text-ink shadow-pop focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to content
      </a>
      <NavRail slug={slug} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenSearch={() => setPaletteOpen(true)} />
        {/* A training partner's shout runs across the top of Today. */}
        {slug && /^\/a\/[^/]+\/today\/?$/.test(pathname) && <TodayShout slug={slug} />}
        <main
          id="main"
          tabIndex={-1}
          className="mx-auto outline-none w-full max-w-[1320px] flex-1 overflow-x-clip px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-5 min-[900px]:px-6 min-[900px]:pb-8 min-[900px]:pt-6"
        >
          {/* Keyed on the path so each page's blocks rise in on navigation (see .gd-stagger). */}
          {slug && <PlanStrip />}
          <div key={pathname} className="gd-stagger">
            {children}
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
